import { z } from 'zod';
import { ComplianceApiErrorResponseZod } from 'src/api_openapi/v2/compliance/schemas';

const CONFIG_PATH = '/compliance/v1/exclusion/config';

const SelfExclusionConfigurationResponseZod = z.object({
    universe: z.string(),
    manualExclusionEmailEnabled: z.boolean(),
    coolingOffHours: z.number().optional(),
    lockedAccountProperties: z.array(z.string()).optional(),
});

export type SelfExclusionConfigType = {
    coolingOffHours: number | null;
    lockedAccountProperties: string[];
    manualExclusionEmailEnabled: boolean;
};

export type SelfExclusionConfigResultType =
    | { type: 'ok'; config: SelfExclusionConfigType }
    | { type: 'error'; message: string };

const toConfig = (body: z.infer<typeof SelfExclusionConfigurationResponseZod>): SelfExclusionConfigType => {
    return {
        coolingOffHours: body.coolingOffHours ?? null,
        lockedAccountProperties: body.lockedAccountProperties ?? [],
        manualExclusionEmailEnabled: body.manualExclusionEmailEnabled,
    };
};

const messageFromBody = (body: unknown, fallback: string): string => {
    if (typeof body === 'string' && body !== '') {
        return body;
    }

    const parsed = ComplianceApiErrorResponseZod.safeParse(body);
    if (parsed.success && parsed.data.error.message !== '') {
        return parsed.data.error.message;
    }

    return fallback;
};

const readBody = async (response: Response): Promise<unknown> => {
    const text = await response.text();
    if (text === '') {
        return null;
    }

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

const requestConfig = async (
    apiUrl: string,
    token: string,
    fallback: string,
    init: RequestInit
): Promise<SelfExclusionConfigResultType> => {
    try {
        const response = await fetch(`${apiUrl}${CONFIG_PATH}`, {
            ...init,
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                authorization: `Bearer ${token}`,
            },
            credentials: 'include',
        });
        const body = await readBody(response);

        if (response.status !== 200) {
            return { type: 'error', message: messageFromBody(body, fallback) };
        }

        const parsed = SelfExclusionConfigurationResponseZod.safeParse(body);
        if (!parsed.success) {
            return { type: 'error', message: fallback };
        }

        return { type: 'ok', config: toConfig(parsed.data) };
    } catch {
        return { type: 'error', message: fallback };
    }
};

export const getSelfExclusionConfig = async (apiUrl: string, token: string): Promise<SelfExclusionConfigResultType> => {
    return requestConfig(apiUrl, token, 'Failed to load self exclusion configuration.', { method: 'GET' });
};

export const updateSelfExclusionEmail = async (
    apiUrl: string,
    token: string,
    manualExclusionEmailEnabled: boolean
): Promise<SelfExclusionConfigResultType> => {
    return requestConfig(apiUrl, token, 'Failed to save self exclusion configuration.', {
        method: 'PATCH',
        body: JSON.stringify({ manualExclusionEmailEnabled }),
    });
};
